import { useProgramAnalytics } from '@/state/program/useProgramAnalytics';
import { Gauge } from 'lucide-react';
import { DisclosureButton, EmptyState, Panel } from '../ConsolePanel';
import { FirstRunLink, FirstRunState } from '../FirstRunState';
import { Header } from './Header';
import { KpiTiles } from './KpiTiles';
import { VelocityChart } from './VelocityChart';
import { BreakdownTable } from './BreakdownTable';
import { ConcludedTable } from './ConcludedTable';

const DESCRIPTION = 'What the testing programme has concluded, and how fast it is learning.';

/**
 * The programme looking back, beside Health and Performance looking at now.
 *
 * Separate from Performance on purpose: that screen reads running experiments'
 * live trends, this one reads concluded experiments' outcomes, and mixing them would
 * put a provisional number next to a final one with nothing to tell them apart.
 */
export const ProgramScreen = () => {
  const manager = useProgramAnalytics();
  const { isLoading, isError, refetch, summary } = manager;

  if (isLoading) {
    return (
      <Panel title="Program" description={DESCRIPTION}>
        <EmptyState>Loading the programme's history…</EmptyState>
      </Panel>
    );
  }

  if (isError || !summary) {
    return (
      <Panel title="Program" description="Could not load the programme's history.">
        <div className="flex flex-col items-start gap-2">
          <EmptyState>Nothing was loaded, so this page cannot say how the programme is doing.</EmptyState>
          <DisclosureButton onClick={() => refetch()}>Try again</DisclosureButton>
        </div>
      </Panel>
    );
  }

  const nothing = summary.launched === 0 && summary.concluded === 0 && summary.inFlight === 0;

  return (
    <div className="flex flex-col gap-3.5">
      <Header manager={manager} />

      {nothing ? (
        <FirstRunState
          icon={Gauge}
          title="No experiments in this window"
          description="This page looks back over your testing programme: how many experiments you started and concluded, and how fast you are learning. Nothing started, ran or concluded in the window you chose. Try a longer one, or start an experiment."
          action={<FirstRunLink to="/experiments/library">Go to experiments</FirstRunLink>}
        />
      ) : (
        <>
          <KpiTiles summary={summary} />
          <VelocityChart months={summary.months} />

          <div className="grid gap-3.5 xl:grid-cols-2" data-tour="program-breakdown">
            <BreakdownTable
              title="By expected mechanism"
              description="On the statistics alone. A test with several mechanisms counts under each."
              rows={summary.mechanisms}
            />
            <BreakdownTable
              title="By PXL importance"
              description="Whether prioritisation predicts which tests win, on the statistics alone."
              rows={summary.pxlBands}
            />
          </div>

          <ConcludedTable manager={manager} />
        </>
      )}
    </div>
  );
};
